import Button from '@/common/components/Button';
import Modal from '@/common/components/Modal';
import SelectController from '@/common/components/SelectController';
import { useCalendarTimezones } from '@/common/hooks/queries/useCalendarTimezones';
import { SettingsIcon } from 'lucide-react';
import { useEffect, useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import useDebounce from '@/common/hooks/useDebounce';
import { Mapper } from '@/common/services/mapper';
import { CalendarTimezoneTransformer, formatTimezoneAcronym } from '@/common/services/calendarTimezoneTransformer';
import { ModalMessageSimple } from '@/common/components/ModalMessageSimple';
import { useQueryClient } from '@tanstack/react-query';
import { useUpdateEstablishmentCompany } from '../../hooks/useUpdateEstablishmentCompany';
import { EstablishmentCompanyData } from '@/common/types/establishment/company.type';
import { updateEstablishmentCompanySchema, UpdateEstablishmentCompanyValidationSchema } from '../../schemas/updateEstablishmentCompany.schema';

interface EstablishmentTimezoneSectionProps {
  establishment?: EstablishmentCompanyData;
  isEstablishmentPending?: boolean;
}

export const EstablishmentTimezoneSection = ({ establishment, isEstablishmentPending }: EstablishmentTimezoneSectionProps) => {
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);

  const queryClient = useQueryClient();

  const [messages, setMessages] = useState<string[] | boolean>(false);

  const {
    mutate: updateCompany,
    isPending: isUpdateCompanyPending,
    isError: isUpdateCompanyError,
  } = useUpdateEstablishmentCompany({
    establishmentCompanyId: establishment?.id,
    onSuccess: () => {
      setMessages(['Fuso horário atualizado com sucesso']);
      queryClient.invalidateQueries({ queryKey: ['establishmentCompany', establishment?.id] });
      setIsEditModalOpen(false);
    },
    onError: (error) => {
      setMessages(error.response?.data.errors.base || ['Ocorreu um erro ao atualizar fuso horário']);
    },
  });

  const { control, reset, handleSubmit } = useForm<UpdateEstablishmentCompanyValidationSchema>({
    resolver: zodResolver(updateEstablishmentCompanySchema),
    defaultValues: {
      time_zone_id: establishment?.time_zone ? CalendarTimezoneTransformer.optionsTransformer()([establishment.time_zone])[0] : undefined,
    },
  });

  const onSubmit: SubmitHandler<UpdateEstablishmentCompanyValidationSchema> = (data) => updateCompany(data);

  const [searchTimezone, setSearchTimezone] = useDebounce('');

  const { data: timezones } = useCalendarTimezones({
    filters: { search: searchTimezone },
    mapper: Mapper.mapWithTransformer({ transformer: CalendarTimezoneTransformer.optionsTransformer() })
  });

  useEffect(() => {
    reset({
      time_zone_id: establishment?.time_zone ? CalendarTimezoneTransformer.optionsTransformer()([establishment.time_zone])[0] : undefined,
    });
  }, [isEstablishmentPending]);

  return (
    <>
      <div className="bg-white-200 rounded-lg p-4 space-y-2">
        <p className="text-[11px] leading-3">Fuso horário da empresa</p>

        <div className="flex items-center gap-2">
          <p className="text-sm">
            {establishment?.time_zone ? `${establishment.time_zone.display_name || establishment.time_zone.identifier} (GMT${formatTimezoneAcronym(establishment.time_zone.acronym)})` : 'Não definido'}
          </p>

          <Button variant="link-white" onClick={() => setIsEditModalOpen(true)}>
            <SettingsIcon strokeWidth={1} className="size-4" />
          </Button>
        </div>
      </div>

      <Modal
        isOpen={isEditModalOpen}
        setIsOpen={setIsEditModalOpen}
        title="Editar fuso horário da empresa"
        body={(
          <div className='text-white-800'>
            <SelectController
              control={control}
              name="time_zone_id"
              label="Fuso horário"
              options={timezones}
              onInputChange={setSearchTimezone}
            />
          </div>
        )}
        useCancellableFooter
        cancelButtonClassName='bg-white-100'
        confirmButtonClassName='bg-primary-blue'
        onConfirm={handleSubmit(onSubmit)}
        confirmButtonIsLoading={isUpdateCompanyPending}
      />

      {messages && (
        <ModalMessageSimple
          messages={messages}
          setMessages={setMessages}
          isError={isUpdateCompanyError}
        />
      )}
    </>
  );
};
